/**
 * Per-device persistence of the signed-in provider's SELECTED IPA for the
 * Patient Priority Index worklist (P6c multi-IPA picker) — mirrors
 * npiStorage.ts exactly: same oid-keyed convention (a different provider
 * signing in on the same device never inherits another identity's choice),
 * same PHI posture (an IPA code is a tenant/opaque identifier — ACT-1037 —
 * not member PHI, so persisting it doesn't violate the "never persist
 * member/search PHI" invariant documented elsewhere in this repo).
 *
 * Storage-layer only: this module does NOT validate that a stored IPA is
 * still one of the provider's CURRENT ipas (permission grants can change) —
 * that check lives in useWorklistAccessGate.ts, which falls back to the
 * picker if the stored value is no longer in the live `ipas` list.
 */

const STORAGE_PREFIX = "member-360:worklist-ipa:";

function storageKey(oid: string): string {
  return `${STORAGE_PREFIX}${oid}`;
}

/** Reads the persisted selected IPA for `oid`, or `null` if absent/unavailable. */
export function getStoredIpa(oid: string | null | undefined): string | null {
  if (!oid || typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(storageKey(oid));
  } catch {
    return null;
  }
}

/** Persists the selected `ipa` for `oid`. Best-effort — a storage failure is silent. */
export function setStoredIpa(oid: string, ipa: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(oid), ipa);
  } catch {
    /* best-effort */
  }
}

/** Clears the persisted selected IPA for `oid` ("Change IPA" affordance). */
export function clearStoredIpa(oid: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(oid));
  } catch {
    /* best-effort */
  }
}
